import { useMutation } from "@tanstack/react-query"
import type {
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  UpdateIngestionSettingsMutationRequest,
  UpdateIngestionSettingsMutationResponse,
} from "../types/UpdateIngestionSettings"

type UpdateIngestionSettingsClient = typeof client<
  UpdateIngestionSettingsMutationResponse,
  Error,
  UpdateIngestionSettingsMutationRequest
>
type UpdateIngestionSettings = {
  data: UpdateIngestionSettingsMutationResponse
  error: Error
  request: UpdateIngestionSettingsMutationRequest
  pathParams: never
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<UpdateIngestionSettingsClient>>
  client: {
    parameters: Partial<Parameters<UpdateIngestionSettingsClient>[0]>
    return: Awaited<ReturnType<UpdateIngestionSettingsClient>>
  }
}
/**
 * @description Update ingestion settings
 * @link /v1/client-settings/update-ingestion-settings
 */
export function useUpdateIngestionSettings(
  options: {
    mutation?: UseMutationOptions<
      UpdateIngestionSettings["response"],
      UpdateIngestionSettings["error"],
      UpdateIngestionSettings["request"]
    >
    client?: UpdateIngestionSettings["client"]["parameters"]
  } = {}
): UseMutationResult<
  UpdateIngestionSettings["response"],
  UpdateIngestionSettings["error"],
  UpdateIngestionSettings["request"]
> {
  const { mutation: mutationOptions, client: clientOptions = {} } =
    options ?? {}
  return useMutation<
    UpdateIngestionSettings["response"],
    UpdateIngestionSettings["error"],
    UpdateIngestionSettings["request"]
  >({
    mutationFn: async (data) => {
      const res = await client<
        UpdateIngestionSettings["data"],
        UpdateIngestionSettings["error"],
        UpdateIngestionSettings["request"]
      >({
        method: "post",
        url: `/v1/client-settings/update-ingestion-settings`,
        data,
        ...clientOptions,
      })
      return res
    },
    ...mutationOptions,
  })
}
